import React, { useState, useMemo, useCallback, useEffect } from 'react';
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
} from 'recharts';
import {
  TrendingUp,
  TrendingDown,
  Search,
  DollarSign,
  FileText,
  Briefcase,
  Layers,
  Activity,
  ArrowRightLeft,
  TrendingUpDown,
  BarChart3,
  Users,
  Megaphone,
} from 'lucide-react';
import Card from '../components/ui/Card';
import KPICard from '../components/ui/KPICard';
import SEO from '../components/SEO';

import axios from 'axios';
import { useLocation } from 'react-router-dom';
import { PETROBRAS_CDCVM } from './stocks/dataStore';
import { TRANSLATIONS, fmtBRL, translateTerm as translateTermUtil } from './stocks/utils';

import OverviewTab from './stocks/tabs/OverviewTab';
import GovernanceTab from './stocks/tabs/GovernanceTab';
import ShareholdersTab from './stocks/tabs/ShareholdersTab';
import FinancialsTab from './stocks/tabs/FinancialsTab';
import AssetsTab from './stocks/tabs/AssetsTab';
import TransactionsTab from './stocks/tabs/TransactionsTab';
import CapitalChangesTab from './stocks/tabs/CapitalChangesTab';
import DistributionTab from './stocks/tabs/DistributionTab';
import PeopleTab from './stocks/tabs/PeopleTab';
import AnnouncementsTab from './stocks/tabs/AnnouncementsTab';

// Top 10 companies shown in the picker with live prices
const TOP_COMPANIES = [
  { cd_cvm: 9512,  nome: 'Petrobras',  ticker: 'PETR4.SA', sigla: 'PETR4' },
  { cd_cvm: 4170,  nome: 'Vale',        ticker: 'VALE3.SA', sigla: 'VALE3' },
  { cd_cvm: 19348, nome: 'Itaú',        ticker: 'ITUB4.SA', sigla: 'ITUB4' },
  { cd_cvm: 906,   nome: 'Bradesco',    ticker: 'BBDC4.SA', sigla: 'BBDC4' },
  { cd_cvm: 21610, nome: 'B3',          ticker: 'B3SA3.SA', sigla: 'B3SA3' },
  { cd_cvm: 20087, nome: 'Embraer',     ticker: 'EMBJ3.SA', sigla: 'EMBR3' },
  { cd_cvm: 13986, nome: 'Suzano',      ticker: 'SUZB3.SA', sigla: 'SUZB3' },
  { cd_cvm: 23264, nome: 'Ambev',       ticker: 'ABEV3.SA', sigla: 'ABEV3' },
  { cd_cvm: 22187, nome: 'PRIO',        ticker: 'PRIO3.SA', sigla: 'PRIO3' },
  { cd_cvm: 5410,  nome: 'WEG',         ticker: 'WEGE3.SA', sigla: 'WEGE3' },
];

// Yahoo Finance tickers for well-known CVM companies (cd_cvm → ticker)
const COMMON_TICKERS = {
  9512:  'PETR4.SA',  // Petrobras
  4170:  'VALE3.SA',  // Vale
  19348: 'ITUB4.SA',  // Itaú Unibanco
  906:   'BBDC4.SA',  // Bradesco
  21610: 'B3SA3.SA',  // B3
  20087: 'EMBJ3.SA',  // Embraer (Yahoo Finance symbol)
  13986: 'SUZB3.SA',  // Suzano
  23264: 'ABEV3.SA',  // Ambev
  22187: 'PRIO3.SA',  // PRIO
  5410:  'WEGE3.SA',  // WEG
  19739: 'RENT3.SA',  // Localiza
  24821: 'RDOR3.SA',  // Rede D'Or
};

export default function StocksPage() {
  const [lang, setLang] = useState('pt');
  const [year, setYear] = useState(null);
  const [availableYears, setAvailableYears] = useState([]);
  const [isPlaying, setIsPlaying] = useState(false);
  const [activeTab, setActiveTab] = useState('distribution');
  const [timeframe, setTimeframe] = useState('daily');
  const [policyModal, setPolicyModal] = useState(null);
  const [selectedAsset, setSelectedAsset] = useState(null);

  const location = useLocation();
  const [cdcvm, setCdcvm] = useState(location.state?.cdcvm ?? PETROBRAS_CDCVM);
  const [companyMeta, setCompanyMeta] = useState({ nome: 'PETROLEO BRASILEIRO S.A. PETROBRAS', ticker: 'PETR4.SA', sigla: 'PETR4' });
  const [showPicker, setShowPicker] = useState(false);
  const [companySearch, setCompanySearch] = useState('');
  const [companyResults, setCompanyResults] = useState([]);
  const [topCompaniesData, setTopCompaniesData] = useState([]);
  const [topCompaniesLoading, setTopCompaniesLoading] = useState(false);

  const [petrobrasData, setPetrobrasData] = useState({});
  const [metaLoading, setMetaLoading] = useState(true);
  const [dataLoading, setDataLoading] = useState(false);

  const [marketData, setMarketData] = useState(null);
  const [marketLoading, setMarketLoading] = useState(false);

  const [timelineData, setTimelineData] = useState({ shareholders: [], dividends: [], dividaBruta: [] });

  const t = TRANSLATIONS[lang];
  const translateTerm = useCallback((term) => translateTermUtil(term, lang), [lang]);

  // Fetch multi-year timeline data when company changes
  useEffect(() => {
    axios.get(`/stocks/${cdcvm}/timeline`)
      .then(res => setTimelineData(res.data))
      .catch(() => {});
  }, [cdcvm]);

  // Fetch company metadata + available years when company changes
  useEffect(() => {
    setMetaLoading(true);
    axios.get(`/stocks/${cdcvm}`)
      .then(res => {
        const years = res.data.years_available || [];
        setAvailableYears(years);
        if (years.length) setYear(Math.max(...years));
        
        // Keep companyMeta synced with the selected cdcvm
        const ticker = COMMON_TICKERS[+cdcvm] || '';
        setCompanyMeta({
          nome: res.data.nome || '',
          ticker: ticker,
          sigla: ''
        });
      })
      .finally(() => setMetaLoading(false));
  }, [cdcvm]);

  // Fetch all FRE tables for the selected year + company
  useEffect(() => {
    if (!year) return;
    setDataLoading(true);
    axios.get(`/stocks/${cdcvm}/${year}`)
      .then(res => {
        const d = res.data;
        if (d.cia_aberta) d[`${year}.csv`] = d.cia_aberta;
        setPetrobrasData(d);
      })
      .catch(() => setPetrobrasData({}))
      .finally(() => setDataLoading(false));
  }, [year, cdcvm]);

  // Reference date from filing metadata
  const refDate = useMemo(() => {
    const root = petrobrasData[`${year}.csv`]?.[0];
    if (root?.DT_REFER) return root.DT_REFER;
    const firstTable = Object.values(petrobrasData).find(v => Array.isArray(v) && v[0]?.Data_Referencia);
    return firstTable?.[0]?.Data_Referencia || null;
  }, [petrobrasData, year]);

  // Fetch Yahoo Finance market data when timeframe or ticker changes
  useEffect(() => {
    if (!companyMeta.ticker) { setMarketData(null); setMarketLoading(false); return; }
    setMarketLoading(true);
    axios.get(`/stocks/market/${companyMeta.ticker}?timeframe=${timeframe}`)
      .then(res => setMarketData(res.data))
      .catch(() => setMarketData(null))
      .finally(() => setMarketLoading(false));
  }, [timeframe, companyMeta.ticker]);

  // Fetch live prices for top companies (on mount + when picker opens)
  useEffect(() => {
    if (topCompaniesData.length) return;
    setTopCompaniesLoading(true);
    Promise.all(
      TOP_COMPANIES.map(c =>
        axios.get(`/stocks/market/${c.ticker}?timeframe=daily`)
          .then(r => ({ ...c, price: r.data.currentPrice ?? 0, changePct: r.data.dayChangePct ?? 0, currency: r.data.currency ?? 'BRL' }))
          .catch(() => ({ ...c, price: 0, changePct: 0, currency: 'BRL' }))
      )
    ).then(results => { setTopCompaniesData(results); setTopCompaniesLoading(false); });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Debounced company name search
  useEffect(() => {
    if (!companySearch.trim()) { setCompanyResults([]); return; }
    const id = setTimeout(() => {
      axios.get(`/stocks/search?q=${encodeURIComponent(companySearch)}`)
        .then(r => setCompanyResults(r.data))
        .catch(() => setCompanyResults([]));
    }, 300);
    return () => clearTimeout(id);
  }, [companySearch]);

  const handleSelectCompany = (company) => {
    setCdcvm(company.cd_cvm);
    const ticker = COMMON_TICKERS[+company.cd_cvm] || '';
    setCompanyMeta({ nome: company.nome, ticker, sigla: '' });
    setYear(null);
    setAvailableYears([]);
    setPetrobrasData({});
    setTimelineData({ shareholders: [], dividends: [], dividaBruta: [] });
    setMarketData(null);
    setShowPicker(false);
    setCompanySearch('');
    setCompanyResults([]);
  };

  // Auto-play years loop
  useEffect(() => {
    let interval;
    if (isPlaying) {
      interval = setInterval(() => {
        setYear(prevYear => {
          const sorted = [...availableYears].sort((a, b) => a - b);
          if (!sorted.length || prevYear >= sorted[sorted.length - 1]) return sorted[0] ?? prevYear;
          return prevYear + 1;
        });
      }, 1500);
    }
    return () => { if (interval) clearInterval(interval); };
  }, [isPlaying, availableYears]);

  // Advanced financial metrics (cross-year, computed once)
  const stockMetrics = useMemo(() => {
    const currentPrice = marketData?.currentPrice ?? 0;
    const prevClose    = marketData?.prevClose    ?? 0;
    const dayChangePct = marketData?.dayChangePct ?? 0;

    let volatilityAnn = marketData?.volatilityAnn ?? 0;

    let latestEPS = 0, latestBVPS = 0, latestNetIncome = 0, latestRevenue = 0;
    let latestDividends = 0, latestROE = 0, totalShares = 0;

    const d = petrobrasData;
    if (d['informacao_financeira']?.length) {
      const rows    = d['informacao_financeira'];
      const epsRow  = rows.find(r => r.Descricao_Conta?.includes('Resultado Básico por Ação'));
      const bvpsRow = rows.find(r => r.Descricao_Conta?.includes('Valor Patrimonial da Ação'));
      const niRow   = rows.find(r => r.Descricao_Conta?.includes('Resultado Líquido'));
      const revRow  = rows.find(r => r.Descricao_Conta?.includes('Rec. Liq.'));
      if (epsRow)  latestEPS        = parseFloat(epsRow.Valor) || 0;
      if (bvpsRow) latestBVPS       = parseFloat(bvpsRow.Valor) || 0;
      if (niRow)   latestNetIncome  = parseFloat(niRow.Valor) || 0;
      if (revRow)  latestRevenue    = parseFloat(revRow.Valor) || 0;
    }
    if (d['distribuicao_dividendos']?.length) {
      const divRows   = d['distribuicao_dividendos'];
      const periods   = [...new Set(divRows.map(r => r.Data_Fim_Exercicio_Social))].sort();
      const latestDiv = periods.length ? divRows.find(r => r.Data_Fim_Exercicio_Social === periods[periods.length - 1]) : null;
      if (latestDiv) {
        latestDividends = parseFloat(latestDiv.Dividendo_Distribuido_Total) || 0;
        latestROE       = parseFloat(latestDiv.Taxa_Retorno_Patrimonio_Liquido_Emissor) || 0;
      }
    }
    if (d['capital_social']?.length) {
      totalShares = parseFloat(d['capital_social'][0].Quantidade_Total_Acoes) || 0;
    }

    const peRatio      = (currentPrice && latestEPS)       ? currentPrice / latestEPS : 0;
    const pbRatio      = (currentPrice && latestBVPS)      ? currentPrice / latestBVPS : 0;
    const netMargin    = (latestNetIncome && latestRevenue) ? (latestNetIncome / latestRevenue) * 100 : 0;
    const marketCap    = currentPrice * totalShares;
    const dividendYield = (latestDividends && marketCap)   ? (latestDividends / marketCap) * 100 : 0;

    return { currentPrice, prevClose, dayChangePct, volatilityAnn, peRatio, pbRatio, netMargin, roe: latestROE, dividendYield, eps: latestEPS };
  }, [petrobrasData, marketData]);

  // Stock chart data for selected timeframe
  const stockChartData = useMemo(() => {
    if (!marketData) return { points: [], currentPrice: 0, priceChangePct: 0, currency: 'BRL' };
    const pts = marketData.points ?? [];
    let priceChangePct = marketData.dayChangePct ?? 0;
    if (pts.length >= 2) {
      const first = pts[0].price;
      const last  = pts[pts.length - 1].price;
      if (first) priceChangePct = ((last - first) / first) * 100;
    }
    return {
      points:        pts,
      currentPrice:  marketData.currentPrice  ?? 0,
      priceChangePct,
      currency:      marketData.currency      ?? 'BRL',
    };
  }, [marketData]);

  const CustomChartTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    return (
      <div className="bg-[var(--card)] border border-[var(--border)] p-3 rounded-xl shadow-lg">
        <p className="text-xs text-[var(--muted)] font-medium mb-1">{label}</p>
        <p className="text-sm font-bold text-[var(--text)]">
          {stockChartData.currency} {payload[0].value.toFixed(2)}
        </p>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      <SEO
        title="Ações e Empresas Abertas na Bolsa (B3)"
        description="Cotações, indicadores financeiros e formulários de referência (CVM) de empresas listadas na B3, incluindo Petrobras e outras companhias abertas do Brasil."
        path="/stocks"
      />

      {/* ── Ticker Strip ── */}
      <div className="flex items-center rounded-xl border border-[var(--border)] bg-[var(--card)] py-2 overflow-hidden">
        <style>{`
          @keyframes ticker-scroll {
            0%   { transform: translateX(0); }
            100% { transform: translateX(-50%); }
          }
          .stocks-ticker-track {
            animation: ticker-scroll 35s linear infinite;
          }
          .stocks-ticker-track:hover {
            animation-play-state: paused;
          }
        `}</style>
        {/* scrolling content */}
        <div className="flex-1 overflow-hidden">
          <div className="stocks-ticker-track flex gap-4 px-2" style={{ width: 'max-content' }}>
            {[...TOP_COMPANIES, ...TOP_COMPANIES].map((c, idx) => {
              const loaded = topCompaniesData.find(d => d.cd_cvm === c.cd_cvm);
              const price = loaded?.price ?? 0;
              const pct   = loaded?.changePct ?? 0;
              const isPos = pct > 0;
              const isNeg = pct < 0;
              return (
                <button key={`${c.cd_cvm}-${idx}`} onClick={() => handleSelectCompany(c)} className="flex items-center gap-2 flex-shrink-0 px-3 py-1 rounded-lg hover:bg-[var(--accent-soft)] transition-colors">
                  <span className="text-[10px] font-black font-mono text-emerald-500">{c.sigla}</span>
                  {topCompaniesLoading && !loaded ? (
                    <span className="w-16 h-3 bg-gray-200 dark:bg-gray-700 rounded animate-pulse inline-block" />
                  ) : (
                    <>
                      <span className="text-xs font-semibold text-[var(--text)] tabular-nums">
                        {price > 0 ? `R$ ${price.toFixed(2)}` : '—'}
                      </span>
                      <span className={`text-[11px] font-bold tabular-nums flex items-center gap-0.5 ${
                        isPos ? 'text-emerald-500' : isNeg ? 'text-red-500' : 'text-[var(--muted)]'
                      }`}>
                        {isPos ? <TrendingUp size={10} /> : isNeg ? <TrendingDown size={10} /> : null}
                        {pct !== 0 ? `${isPos ? '+' : ''}${pct.toFixed(2)}%` : price > 0 ? '0.00%' : ''}
                      </span>
                    </>
                  )}
                </button>
              );
            })}
          </div>
        </div>
        {/* fixed edit button on the right */}
        <div className="flex-shrink-0 flex items-center pl-6 pr-3" style={{ background: 'linear-gradient(to right, transparent, var(--card) 40%)' }}>
          <button
            onClick={() => setShowPicker(true)}
            title={lang === 'pt' ? 'Buscar empresa' : 'Search company'}
            className="group flex items-center justify-center w-7 h-7 rounded-lg hover:bg-[var(--accent-soft)] transition-colors text-[var(--muted)] hover:text-emerald-500"
          >
            <Search size={14} />
          </button>
        </div>
      </div>

      {/* ── Page Header ── */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 border-b border-[var(--border)] pb-6">
        <div>
          <div className="flex items-center gap-3 flex-wrap">
            <button
              onClick={() => setShowPicker(true)}
              className="group flex items-center gap-2 text-left"
              title={lang === 'pt' ? 'Trocar empresa' : 'Change company'}
            >
              <h1 className="text-3xl font-extrabold tracking-tight text-[var(--text)] group-hover:text-emerald-500 transition-colors">
                {companyMeta.nome}
              </h1>
            </button>
            {companyMeta.sigla && (
              <span className="bg-emerald-500/10 border border-emerald-500/20 text-emerald-500 text-xs px-2.5 py-1 rounded-full font-bold uppercase tracking-wider">
                {companyMeta.sigla}
              </span>
            )}
          </div>
          <p className="text-sm text-[var(--muted)] mt-1.5">
            {year ? t.doc_subtitle.replace('{year}', year) : '…'}
          </p>
        </div>

        <div className="flex items-center gap-3 self-end sm:self-auto flex-wrap">
          {/* Year Dropdown */}
          <div className="relative">
            <select
              value={year || ''}
              onChange={(e) => { setYear(Number(e.target.value)); setIsPlaying(false); }}
              disabled={metaLoading}
              className="appearance-none bg-[var(--card)] border border-[var(--border)] rounded-xl px-4 py-1.5 pr-8 text-xs font-semibold text-[var(--text)] focus:outline-none focus:border-emerald-500 cursor-pointer shadow-sm disabled:opacity-50"
            >
              {[...availableYears].sort((a, b) => b - a).map(y => (
                <option key={y} value={y} className="bg-[var(--card)] text-[var(--text)]">{y}</option>
              ))}
            </select>
            <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2.5 text-[var(--muted)]">
              <svg className="fill-current h-3 w-3" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
                <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z"/>
              </svg>
            </div>
          </div>

          {/* Language Toggle */}
          <div className="flex items-center bg-[var(--card)] border border-[var(--border)] p-1 rounded-xl shadow-sm">
            <button
              onClick={() => setLang('pt')}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold tracking-wide transition-all ${lang === 'pt' ? 'bg-emerald-500 text-white shadow-sm' : 'text-[var(--muted)] hover:text-[var(--text)]'}`}
            >
              Português
            </button>
            <button
              onClick={() => setLang('en')}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold tracking-wide transition-all ${lang === 'en' ? 'bg-emerald-500 text-white shadow-sm' : 'text-[var(--muted)] hover:text-[var(--text)]'}`}
            >
              English
            </button>
          </div>
        </div>
      </div>

      {/* ── KPI Grid ── */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KPICard
          label={t.stat_prev_close}
          value={fmtBRL(stockMetrics.prevClose)}
          change={`${stockMetrics.dayChangePct > 0 ? '+' : ''}${stockMetrics.dayChangePct.toFixed(2)}%`}
          changeType={stockMetrics.dayChangePct >= 0 ? 'positive' : 'negative'}
          tooltip={t.tooltip_prev_close}
          className="border border-[var(--border)]"
        />
        <KPICard label={t.stat_pe_ratio}   value={`${stockMetrics.peRatio.toFixed(2)}x`}           tooltip={t.tooltip_pe_ratio}    className="border border-[var(--border)]" />
        <KPICard label={t.stat_pb_ratio}   value={`${stockMetrics.pbRatio.toFixed(2)}x`}           tooltip={t.tooltip_pb_ratio}    className="border border-[var(--border)]" />
        <KPICard label={t.stat_roe}        value={`${stockMetrics.roe.toFixed(2)}%`}               tooltip={t.tooltip_roe}         className="border border-[var(--border)]" />
        <KPICard label={t.stat_net_margin} value={`${stockMetrics.netMargin.toFixed(2)}%`}         tooltip={t.tooltip_net_margin}  className="border border-[var(--border)]" />
        <KPICard label={t.stat_div_yield}  value={`${stockMetrics.dividendYield.toFixed(2)}%`}     tooltip={t.tooltip_div_yield}   className="border border-[var(--border)]" />
        <KPICard label={t.stat_volatility} value={`${stockMetrics.volatilityAnn.toFixed(2)}%`}     tooltip={t.tooltip_volatility}  className="border border-[var(--border)]" />
        <KPICard label="EPS / Lucro por Ação" value={fmtBRL(stockMetrics.eps)}                     tooltip={t.tooltip_eps}         className="border border-[var(--border)]" />
      </div>

      {/* ── Stock Price Chart ── */}
      <Card className="border border-[var(--border)] overflow-hidden">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
          <div>
            <div className="flex items-center gap-2">
              <span className="flex items-center justify-center w-6 h-6 rounded-lg bg-emerald-500 text-white text-xs font-black">BR</span>
              <h3 className="text-lg font-bold text-[var(--text)]">
                {companyMeta.ticker
                  ? `${companyMeta.ticker} — ${lang === 'pt' ? 'Preço de Mercado' : 'Market Price'}`
                  : (lang === 'pt' ? 'Preço de Mercado' : 'Market Price')}
              </h3>
            </div>
            <p className="text-xs text-[var(--muted)] mt-0.5">{t.stock_subtitle}</p>
          </div>

          <div className="flex items-center gap-4 w-full md:w-auto justify-between md:justify-end">
            {companyMeta.ticker ? (
              <>
                <div className="text-right">
                  <div className="text-2xl font-extrabold text-[var(--text)]">
                    {marketLoading
                      ? <span className="text-[var(--muted)] text-base animate-pulse">Carregando…</span>
                      : <>{stockChartData.currency} {stockChartData.currentPrice.toFixed(2)}</>}
                  </div>
                  <div className={`text-xs font-bold flex items-center justify-end gap-1 mt-0.5 ${stockChartData.priceChangePct >= 0 ? 'text-emerald-500' : 'text-red-500'}`}>
                    {stockChartData.priceChangePct >= 0 ? <TrendingUp size={12} /> : <TrendingDown size={12} />}
                    {stockChartData.priceChangePct >= 0 ? '+' : ''}{stockChartData.priceChangePct.toFixed(2)}%
                  </div>
                </div>

                <div className="flex bg-[var(--accent-soft)] p-1 rounded-xl border border-[var(--border)]">
                  {[
                    { id: 'hourly',  label: t.btn_hourly  },
                    { id: 'daily',   label: t.btn_daily   },
                    { id: 'monthly', label: t.btn_monthly },
                    { id: 'yearly',  label: t.btn_yearly  },
                  ].map(tf => (
                    <button
                      key={tf.id}
                      onClick={() => setTimeframe(tf.id)}
                      className={`px-3 py-1 rounded-lg text-xs font-semibold transition-all ${timeframe === tf.id ? 'bg-emerald-500 text-white shadow-sm' : 'text-[var(--muted)] hover:text-[var(--text)]'}`}
                    >
                      {tf.label}
                    </button>
                  ))}
                </div>
              </>
            ) : (
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  placeholder={lang === 'pt' ? 'ex: VALE3.SA' : 'e.g. VALE3.SA'}
                  className="bg-[var(--accent-soft)] border border-[var(--border)] rounded-lg px-3 py-1.5 text-xs text-[var(--text)] placeholder:text-[var(--muted)] focus:outline-none focus:border-emerald-500 w-36"
                  onBlur={e => { if (e.target.value.trim()) setCompanyMeta(m => ({ ...m, ticker: e.target.value.trim() })); }}
                  onKeyDown={e => { if (e.key === 'Enter' && e.target.value.trim()) setCompanyMeta(m => ({ ...m, ticker: e.target.value.trim() })); }}
                />
                <span className="text-[10px] text-[var(--muted)] whitespace-nowrap">
                  {lang === 'pt' ? 'Yahoo Finance ticker' : 'Yahoo Finance ticker'}
                </span>
              </div>
            )}
          </div>
        </div>

        {companyMeta.ticker ? (
          <div className="h-64 w-full">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={stockChartData.points} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                <defs>
                  <linearGradient id="colorStock" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%"  stopColor="#10B981" stopOpacity={0.25} />
                    <stop offset="95%" stopColor="#10B981" stopOpacity={0.0}  />
                  </linearGradient>
                </defs>
                <XAxis dataKey="time" tickLine={false} axisLine={false} tick={{ fill: 'var(--muted)', fontSize: 10 }} />
                <YAxis domain={['auto', 'auto']} tickLine={false} axisLine={false} tick={{ fill: 'var(--muted)', fontSize: 10 }} tickFormatter={v => v.toFixed(2)} />
                <Tooltip content={<CustomChartTooltip />} />
                <Area type="monotone" dataKey="price" stroke="#10B981" strokeWidth={2.5} fillOpacity={1} fill="url(#colorStock)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <div className="h-40 flex flex-col items-center justify-center gap-3 rounded-xl border border-dashed border-[var(--border)] bg-[var(--accent-soft)]">
            <p className="text-sm font-semibold text-[var(--text)]">
              {lang === 'pt' ? 'Insira o código Yahoo Finance para ver o preço' : 'Enter a Yahoo Finance ticker to see market price'}
            </p>
            <p className="text-xs text-[var(--muted)]">
              {lang === 'pt'
                ? 'Ex: VALE3.SA, ITUB4.SA, BBDC4.SA'
                : 'E.g. VALE3.SA, ITUB4.SA, BBDC4.SA'}
            </p>
          </div>
        )}
      </Card>

      {/* ── Tab Navigation ── */}
      <nav className="sticky top-0 z-10 bg-[var(--bg)]/95 backdrop-blur-md border-b border-[var(--border)] -mx-4 px-4 sm:-mx-6 sm:px-6">
        <div className="flex gap-1 overflow-x-auto py-3 scrollbar-hide">
          {[
            { id: 'distribution',   label: t.menu_distribution,    icon: BarChart3       },
            { id: 'overview',       label: t.menu_overview,         icon: FileText        },
            { id: 'governance',     label: t.menu_governance,       icon: Briefcase       },
            { id: 'shareholders',   label: t.menu_shareholders,     icon: Layers          },
            { id: 'financials',     label: t.menu_financials,       icon: DollarSign      },
            { id: 'assets',         label: t.menu_assets,           icon: Activity        },
            { id: 'transactions',   label: t.menu_transactions,     icon: ArrowRightLeft  },
            { id: 'capital_changes',label: t.menu_capital_changes,  icon: TrendingUpDown  },
            { id: 'people',         label: t.menu_people,           icon: Users           },
            { id: 'announcements',  label: t.menu_announcements,    icon: Megaphone       },
          ].map(tab => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 whitespace-nowrap px-4 py-2 rounded-full text-xs font-semibold transition-all ${
                  isActive
                    ? 'bg-emerald-500 text-white shadow-sm'
                    : 'text-[var(--muted)] hover:text-[var(--text)] hover:bg-[var(--accent-soft)]'
                }`}
              >
                <Icon size={14} />
                {tab.label}
              </button>
            );
          })}
        </div>
      </nav>

      {/* ── Tab Contents ── */}
      <div className="relative transition-all duration-300">
        {dataLoading && (
          <div className="absolute inset-0 z-10 flex items-center justify-center bg-[var(--bg)]/60 backdrop-blur-sm rounded-xl min-h-[200px]">
            <div className="flex flex-col items-center gap-3">
              <div className="w-8 h-8 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" />
              <p className="text-xs text-[var(--muted)] font-medium">Carregando dados...</p>
            </div>
          </div>
        )}

        {activeTab === 'overview'        && <OverviewTab      lang={lang} year={year} petrobrasData={petrobrasData} refDate={refDate} t={t} setPolicyModal={setPolicyModal} />}
        {activeTab === 'governance'      && <GovernanceTab    lang={lang} year={year} petrobrasData={petrobrasData} refDate={refDate} t={t} />}
        {activeTab === 'shareholders'    && <ShareholdersTab  lang={lang} year={year} petrobrasData={petrobrasData} refDate={refDate} t={t} />}
        {activeTab === 'financials'      && <FinancialsTab    lang={lang} year={year} petrobrasData={petrobrasData} refDate={refDate} t={t} timelineData={timelineData} />}
        {activeTab === 'assets'          && <AssetsTab        lang={lang} year={year} petrobrasData={petrobrasData} refDate={refDate} t={t} setSelectedAsset={setSelectedAsset} timelineData={timelineData} />}
        {activeTab === 'transactions'    && <TransactionsTab  lang={lang} year={year} petrobrasData={petrobrasData} refDate={refDate} t={t} />}
        {activeTab === 'capital_changes' && <CapitalChangesTab lang={lang} year={year} petrobrasData={petrobrasData} refDate={refDate} t={t} />}
        {activeTab === 'distribution'    && <DistributionTab  lang={lang} year={year} petrobrasData={petrobrasData} refDate={refDate} t={t} timelineData={timelineData} />}
        {activeTab === 'people'          && <PeopleTab        lang={lang} year={year} petrobrasData={petrobrasData} refDate={refDate} t={t} />}
        {activeTab === 'announcements'   && <AnnouncementsTab lang={lang} cdcvm={cdcvm} />}

        {/* ── Trading Policy Modal ── */}
        {policyModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
            <div className="absolute inset-0" onClick={() => setPolicyModal(null)} />
            <div className="relative bg-[var(--card)] border border-[var(--border)] rounded-2xl max-w-2xl w-full shadow-2xl flex flex-col max-h-[85vh]">
              <div className="flex justify-between items-center border-b border-[var(--border)] px-6 py-4 flex-shrink-0">
                <div>
                  <h3 className="text-base font-bold text-[var(--text)]">
                    {lang === 'pt' ? 'Política de Negociação' : 'Trading Policy'}
                  </h3>
                  <div className="flex gap-4 mt-1 text-xs text-[var(--muted)]">
                    <span>{lang === 'pt' ? 'Aprovada em:' : 'Approved:'} <strong className="text-yellow-500">{(policyModal.Data_Aprovacao || '').split(' ')[0]}</strong></span>
                    <span>{lang === 'pt' ? 'Órgão:' : 'Authority:'} <strong className="text-[var(--text)]">{policyModal.Orgao_Aprovacao}</strong></span>
                  </div>
                </div>
                <button
                  onClick={() => setPolicyModal(null)}
                  className="text-[var(--muted)] hover:text-[var(--text)] text-xs font-semibold p-1.5 hover:bg-[var(--accent-soft)] rounded-lg transition-all flex-shrink-0"
                >
                  {lang === 'pt' ? 'Fechar' : 'Close'}
                </button>
              </div>

              <div className="overflow-y-auto px-6 py-5 space-y-6 text-sm">
                {policyModal.Principais_Caracteristicas && (
                  <div>
                    <h4 className="text-[10px] text-[var(--muted)] font-semibold uppercase tracking-wider mb-3">
                      {lang === 'pt' ? 'Principais Características' : 'Main Characteristics'}
                    </h4>
                    <p className="text-[var(--text)] leading-relaxed whitespace-pre-wrap text-xs">{policyModal.Principais_Caracteristicas}</p>
                  </div>
                )}
                {policyModal.Vedacao_Procedimentos_Fiscalizacao && (
                  <div className="border-t border-[var(--border)] pt-5">
                    <h4 className="text-[10px] text-[var(--muted)] font-semibold uppercase tracking-wider mb-3">
                      {lang === 'pt' ? 'Vedações e Procedimentos de Fiscalização' : 'Restrictions & Enforcement Procedures'}
                    </h4>
                    <p className="text-[var(--text)] leading-relaxed whitespace-pre-wrap text-xs">{policyModal.Vedacao_Procedimentos_Fiscalizacao}</p>
                  </div>
                )}
              </div>

              <div className="flex justify-end px-6 py-4 border-t border-[var(--border)] flex-shrink-0">
                <button
                  onClick={() => setPolicyModal(null)}
                  className="bg-emerald-500 hover:bg-emerald-600 text-white font-bold text-xs px-5 py-2 rounded-xl shadow-sm transition-all"
                >
                  {lang === 'pt' ? 'Fechar' : 'Close'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ── Asset Details Modal ── */}
        {selectedAsset && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-200">
            <div className="absolute inset-0" onClick={() => setSelectedAsset(null)} />
            <div className="relative bg-[var(--card)] border border-[var(--border)] rounded-2xl max-w-lg w-full p-6 shadow-2xl space-y-4 text-sm animate-in zoom-in-95 duration-200 text-[var(--text)]">
              <div className="flex justify-between items-center border-b border-[var(--border)] pb-3">
                <h3 className="text-base font-bold text-[var(--text)]">{t.modal_title}</h3>
                <button
                  onClick={() => setSelectedAsset(null)}
                  className="text-[var(--muted)] hover:text-[var(--text)] text-xs font-semibold p-1 hover:bg-[var(--accent-soft)] rounded-lg transition-all"
                >
                  {t.close}
                </button>
              </div>

              <div className="space-y-3.5 max-h-[70vh] overflow-y-auto pr-1">
                {selectedAsset.assetType === 'cvm_intang' ? (
                  <>
                    <div>
                      <span className="text-xs text-[var(--muted)] font-medium uppercase tracking-wider block mb-1">{lang === 'pt' ? 'Período' : 'Period'}</span>
                      <p className="font-semibold text-[var(--text)]">{selectedAsset.period}</p>
                    </div>
                    <div>
                      <span className="text-xs text-[var(--muted)] font-medium uppercase tracking-wider block mb-1">{lang === 'pt' ? 'Conta CVM' : 'CVM Account'}</span>
                      <span className="bg-yellow-500/10 border border-yellow-500/20 text-yellow-600 dark:text-yellow-400 text-[10px] px-2.5 py-0.5 rounded font-mono inline-block">
                        {selectedAsset.cdConta}
                      </span>
                    </div>
                    <div>
                      <span className="text-xs text-[var(--muted)] font-medium uppercase tracking-wider block mb-1">{lang === 'pt' ? 'Ativo' : 'Asset'}</span>
                      <p className="font-semibold text-[var(--text)]">{selectedAsset.descricao}</p>
                    </div>
                    <div>
                      <span className="text-xs text-[var(--muted)] font-medium uppercase tracking-wider block mb-1">{lang === 'pt' ? 'Valor' : 'Value'}</span>
                      <p className="font-semibold text-[var(--text)] text-base">
                        {selectedAsset.value != null
                          ? (Math.abs(selectedAsset.value) >= 1e9
                              ? `R$ ${(selectedAsset.value / 1e9).toFixed(2)}B`
                              : `R$ ${(selectedAsset.value / 1e6).toFixed(0)}M`)
                          : '-'}
                      </p>
                    </div>
                    <div>
                      <span className="text-xs text-[var(--muted)] font-medium uppercase tracking-wider block mb-1">{lang === 'pt' ? 'Composição (BPA)' : 'Breakdown (BPA)'}</span>
                      <div className="space-y-1.5 text-[var(--text)]">
                        <div className="flex justify-between text-[11px] border-b border-[var(--border)] pb-1">
                          <span className="font-mono text-yellow-600 dark:text-yellow-400">1.02.04.01</span>
                          <span>{lang === 'pt' ? 'Intangíveis' : 'Intangibles'}</span>
                        </div>
                        <div className="flex justify-between text-[11px] border-b border-[var(--border)] pb-1 pl-3">
                          <span className="font-mono text-yellow-600 dark:text-yellow-400">1.02.04.01.01</span>
                          <span>{lang === 'pt' ? 'Contrato de Concessão' : 'Concession Contract'}</span>
                        </div>
                        <div className="flex justify-between text-[11px] border-b border-[var(--border)] pb-1 pl-3">
                          <span className="font-mono text-yellow-600 dark:text-yellow-400">1.02.04.01.02</span>
                          <span>{lang === 'pt' ? 'Direitos e Concessões / Softwares' : 'Rights & Concessions / Software'}</span>
                        </div>
                        <div className="flex justify-between text-[11px] pb-1">
                          <span className="font-mono text-yellow-600 dark:text-yellow-400">1.02.04.02</span>
                          <span>Goodwill</span>
                        </div>
                      </div>
                      <p className="text-[10px] text-[var(--muted)] mt-2">
                        {lang === 'pt'
                          ? 'A Petrobras consolida o valor total em 1.02.04. Sub-contas reportadas com valor zero no CVM/BPA.'
                          : 'Petrobras consolidates the total in 1.02.04. Sub-accounts reported as zero in CVM/BPA.'}
                      </p>
                    </div>
                    <div>
                      <span className="text-xs text-[var(--muted)] font-medium uppercase tracking-wider block mb-1">{lang === 'pt' ? 'Fonte' : 'Source'}</span>
                      <p className="text-[var(--muted)] text-[11px]">
                        {selectedAsset.source === 'dfp' ? 'CVM/DFP – BPA Consolidado (anual)' : 'CVM/ITR – BPA Consolidado (trimestral)'}
                      </p>
                    </div>
                  </>
                ) : selectedAsset.assetType === 'intangible' ? (
                  <>
                    <div>
                      <span className="text-xs text-[var(--muted)] font-medium uppercase tracking-wider block mb-1">{t.th_asset_type}</span>
                      <span className="bg-yellow-500/10 border border-yellow-500/20 text-yellow-600 dark:text-yellow-400 text-[10px] px-2.5 py-0.5 rounded font-bold uppercase tracking-wider inline-block">
                        {translateTerm(selectedAsset.Tipo_Ativo)}
                      </span>
                    </div>
                    <div>
                      <span className="text-xs text-[var(--muted)] font-medium uppercase tracking-wider block mb-1">{t.th_description}</span>
                      <p className="font-semibold text-[var(--text)] leading-relaxed">{translateTerm(selectedAsset.Descricao_Ativo)}</p>
                    </div>
                    <div>
                      <span className="text-xs text-[var(--muted)] font-medium uppercase tracking-wider block mb-1">{t.th_duration}</span>
                      <p className="font-semibold text-[var(--text)]">{translateTerm(selectedAsset.Duracao)}</p>
                    </div>
                    <div>
                      <span className="text-xs text-[var(--muted)] font-medium uppercase tracking-wider block mb-1">{t.th_loss_event}</span>
                      <p className="font-semibold text-[var(--text)] leading-relaxed">{translateTerm(selectedAsset.Evento_Perda_Direito)}</p>
                    </div>
                    {selectedAsset.Consequencia_Perda_Direito && (
                      <div>
                        <span className="text-xs text-[var(--muted)] font-medium uppercase tracking-wider block mb-1">{t.th_loss_consequence}</span>
                        <p className="font-semibold text-[var(--text)] leading-relaxed">{translateTerm(selectedAsset.Consequencia_Perda_Direito)}</p>
                      </div>
                    )}
                  </>
                ) : (
                  <>
                    <div>
                      <span className="text-xs text-[var(--muted)] font-medium uppercase tracking-wider block mb-1">{t.th_asset_desc}</span>
                      <p className="font-semibold text-[var(--text)] leading-relaxed">{translateTerm(selectedAsset.Descricao_Bem_Ativo)}</p>
                    </div>
                    <div>
                      <span className="text-xs text-[var(--muted)] font-medium uppercase tracking-wider block mb-1">{t.th_location}</span>
                      <p className="font-semibold text-[var(--text)]">
                        {selectedAsset.Municipio || ''} - {selectedAsset.UF || ''}, {translateTerm(selectedAsset.Pais)}
                      </p>
                    </div>
                    <div>
                      <span className="text-xs text-[var(--muted)] font-medium uppercase tracking-wider block mb-1">{t.th_prop_type}</span>
                      <p className="font-semibold text-[var(--text)]">{translateTerm(selectedAsset.Tipo_Propriedade)}</p>
                    </div>
                  </>
                )}
              </div>

              <div className="flex justify-end pt-2">
                <button
                  onClick={() => setSelectedAsset(null)}
                  className="bg-emerald-500 hover:bg-emerald-600 text-white font-bold text-xs px-4 py-2 rounded-xl shadow-sm transition-all"
                >
                  {t.close}
                </button>
              </div>
            </div>
          </div>
        )}

      </div>

      {/* ── Company Picker Modal ── */}
      {showPicker && (
        <div className="fixed inset-0 z-50 flex items-start justify-center pt-20 bg-black/50 backdrop-blur-sm" onClick={() => setShowPicker(false)}>
          <div className="bg-[var(--card)] border border-[var(--border)] rounded-2xl shadow-2xl w-full max-w-lg mx-4" onClick={e => e.stopPropagation()}>
            <div className="p-4 border-b border-[var(--border)]">
              <p className="text-xs font-semibold text-[var(--muted)] uppercase tracking-wider mb-2">
                {lang === 'pt' ? 'Buscar Empresa' : 'Search Company'}
              </p>
              <input
                autoFocus
                type="text"
                value={companySearch}
                onChange={e => setCompanySearch(e.target.value)}
                placeholder={lang === 'pt' ? 'Nome da empresa...' : 'Company name...'}
                className="w-full bg-[var(--accent-soft)] border border-[var(--border)] rounded-xl px-4 py-2.5 text-sm text-[var(--text)] focus:outline-none focus:border-emerald-500"
              />
            </div>
            <div className="max-h-80 overflow-y-auto divide-y divide-[var(--border)]">
              {companyResults.map(c => (
                <button
                  key={c.cd_cvm}
                  onClick={() => handleSelectCompany(c)}
                  className="w-full text-left px-4 py-3 hover:bg-[var(--accent-soft)] transition-colors"
                >
                  <div className="font-semibold text-sm text-[var(--text)] truncate">{c.nome}</div>
                  <div className="text-xs text-[var(--muted)] mt-0.5">CVM {c.cd_cvm} · {c.cnpj}</div>
                </button>
              ))}
              {companySearch.trim() && !companyResults.length && (
                <p className="px-4 py-8 text-sm text-center text-[var(--muted)]">
                  {lang === 'pt' ? 'Nenhuma empresa encontrada' : 'No companies found'}
                </p>
              )}
              {!companySearch.trim() && (
                topCompaniesLoading
                  ? <p className="px-4 py-8 text-sm text-center text-[var(--muted)] animate-pulse">
                      {lang === 'pt' ? 'Carregando cotações...' : 'Loading prices...'}
                    </p>
                  : <>
                      <div className="px-4 pt-3 pb-1">
                        <p className="text-[10px] font-semibold text-[var(--muted)] uppercase tracking-wider">
                          {lang === 'pt' ? 'Principais empresas' : 'Top companies'}
                        </p>
                      </div>
                      {topCompaniesData.map(c => (
                        <button
                          key={c.cd_cvm}
                          onClick={() => handleSelectCompany(c)}
                          className="w-full text-left px-4 py-2.5 hover:bg-[var(--accent-soft)] transition-colors"
                        >
                          <div className="flex items-center justify-between gap-3">
                            <div className="flex items-center gap-2.5 min-w-0">
                              <span className="text-[10px] font-black font-mono bg-emerald-500/10 text-emerald-500 border border-emerald-500/20 px-1.5 py-0.5 rounded shrink-0">
                                {c.sigla}
                              </span>
                              <span className="text-sm font-medium text-[var(--text)] truncate">{c.nome}</span>
                            </div>
                            <div className="text-right shrink-0">
                              {c.price > 0
                                ? <>
                                    <div className="text-sm font-bold text-[var(--text)]">
                                      R$ {c.price.toFixed(2)}
                                    </div>
                                    <div className={`text-[11px] font-semibold ${
                                      c.changePct >= 0 ? 'text-emerald-500' : 'text-red-500'
                                    }`}>
                                      {c.changePct >= 0 ? '+' : ''}{c.changePct.toFixed(2)}%
                                    </div>
                                  </>
                                : <span className="text-xs text-[var(--muted)]">—</span>
                              }
                            </div>
                          </div>
                        </button>
                      ))}
                    </>
              )}
            </div>
            <div className="p-3 border-t border-[var(--border)] flex justify-end">
              <button
                onClick={() => { setShowPicker(false); setCompanySearch(''); }}
                className="text-xs font-semibold text-[var(--muted)] hover:text-[var(--text)] px-3 py-1.5 hover:bg-[var(--accent-soft)] rounded-lg transition-all"
              >
                {lang === 'pt' ? 'Cancelar' : 'Cancel'}
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
